import React from "react";
import { RCCGrid, RCCInput, RCCRadio, RCCSwitch} from "react-common-components";
import { useTranslation } from "src/context/Translations/context";
import chart from "src/assets/icons/chart.svg";
import stackedChart from "src/assets/icons/stacked-chart.svg";
import Title from "src/components/Title";
import { useWatch } from "react-hook-form";
import InputNumber from "src/components/InputNumber";
import PalettePicker from "src/components/PalettePicker";
import { GraphicColors, GraphicComp, GraphicOrientation, ITab } from "../interface";
import { FieldAlwaysOnRight, FieldOnRight } from "../styles";

const TabGraphic: React.FC<ITab> = ({ 
	indicator,
	register,
	control,
	errors,
	hidden
}) => {
	if(hidden) return null;

	const { translate} = useTranslation();

	const watchComp = useWatch({
		control,
		name: "indicator.iqc001Compgrafico",
		defaultValue: GraphicComp.SIDE_BY_SIDE
	});
	
	return(
		<RCCGrid container item 
			justifyContent="center">
			<RCCGrid container item 
				xs={12} 
				sm={10} 
				lg={8}
			>
				<RCCGrid container item>
					<Title 
						size="md"
						color="purple"
					>
						{translate("Defina as opções de visualização do gráfico")}
					</Title>
				</RCCGrid>	
				<RCCGrid container item 
					xs={12} 
					sm={6}
					lg={4}
				>
					<RCCRadio 
						name="indicator.iqc001Compgrafico"
						label={translate("Composição")}
						register={register}
						color="purple"
						direction="vertical" 
						options={[
							{value:GraphicComp.SIDE_BY_SIDE,label:translate("Lado a lado")},
							{value:GraphicComp.STACK,label:translate("Empilhado")}
						]}
						defaultValue={indicator.iqc001Compgrafico?.toString()||GraphicComp.SIDE_BY_SIDE}
					/>
					<img 	
						src={watchComp === GraphicComp.SIDE_BY_SIDE ? chart:stackedChart} 
						alt="Chart" 
						style={{width:"4rem", marginLeft:"0.5rem"}}
					/>
				</RCCGrid>
				<RCCGrid container item 
					xs={12} 
					sm={6}
					md={4}
				>
					<RCCRadio 
						name="indicator.iqc001Corgrafico"
						label={translate("Cores gráfico")}
						register={register}
						color="purple"
						direction="vertical" 
						options={[
							{value:GraphicColors.METRICS,label:translate("Conforme métricas")},
							{value:GraphicColors.SUBTITLE,label:translate("Conforme legenda")}
						]}
						defaultValue={indicator.iqc001Corgrafico?.toString()||GraphicColors.METRICS}
					/>
				</RCCGrid>
				<RCCGrid container item 
					xs={12} 
					sm={6}
					md={4}
				>
					<RCCRadio 
						name="indicator.iqc001Labelvalores"
						label={translate("Orientação valores no gráfico")}
						register={register}
						color="purple"
						direction="vertical" 
						options={[
							{value:GraphicOrientation.HORIZONTAL,label:translate("Horizontal")},
							{value:GraphicOrientation.VERTICAL,label:translate("Vertical")}
						]}
						defaultValue={indicator.iqc001Labelvalores?.toString()||GraphicOrientation.HORIZONTAL}
					/>
				</RCCGrid>
				<RCCGrid container item>
					<PalettePicker
						control={control}
						name="indicator.iqc001PaletaCores"
						defaultValue={indicator?.iqc001PaletaCores ? indicator?.iqc001PaletaCores?.toString() : "1"}
					/>
				</RCCGrid>
				<RCCGrid container item>
					<RCCSwitch
						name="indicator.iqc001Tendencia"
						register={register}
						label={translate("Linha de tendência")}
						defaultChecked={indicator.iqc001Tendencia}
						labelPosition="end"
					/>
				</RCCGrid>
				<hr/>
				<RCCGrid container item>
					<RCCGrid container item>
						<Title 
							size="md" 
							color="purple" 
							marginTop={10}
						>
							{translate("Eixo Y esquerda")}
						</Title>
					</RCCGrid>	
					<RCCGrid container item>
						<Title 
							size="xs"
							color="grey"
						>
							{translate("Na tab de campos você pode configurar se o campo será do eixo Y da esquerda ou direita")}.
						</Title>
					</RCCGrid>
					<RCCGrid container item>
						<RCCGrid container item 
							xs={12} 
							sm={6}
						>
							<RCCInput 
								name="indicator.iqc001Roteixoesq"
								register={register}
								label={translate("Rótulo")}
								defaultValue={indicator.iqc001Roteixoesq}
								error={errors?.indicator?.iqc001Roteixoesq}
								maxLength={60}
							/>
						</RCCGrid>
						<RCCGrid container item 
							styleInline={FieldOnRight}
							wrap="nowrap"
							sm={6}
						>
							<RCCGrid container item >
								<InputNumber 
									name="indicator.iqc001Escalamin"
									control={control}
									decimalScale={0}
									label={translate("Escala mín.")}
									defaultValue={indicator.iqc001Escalamin}
									error={errors?.indicator?.iqc001Escalamin}
									maxLength={10}
								/>
							</RCCGrid>
							<RCCGrid container item styleInline={FieldAlwaysOnRight}>
								<InputNumber 
									name="indicator.iqc001Escalamax"
									control={control}
									decimalScale={0}
									label={translate("Escala max.")}
									defaultValue={indicator.iqc001Escalamax}
									error={errors?.indicator?.iqc001Escalamax}
									maxLength={10}
								/>
							</RCCGrid>
						</RCCGrid>
					</RCCGrid>
				</RCCGrid>
				<hr/>
				<RCCGrid container item>
					<RCCGrid container item>
						<Title 
							size="md" 
							color="purple" 
							marginTop={10}
						>
							{translate("Eixo Y direita")}
						</Title>
					</RCCGrid>	
					<RCCGrid container item>
						<Title 
							size="xs"
							color="grey"
						>
							{translate("Na tab de campos você pode configurar se o campo será do eixo Y da esquerda ou direita")}.
						</Title>
					</RCCGrid>
					<RCCGrid container item 
						justifyContent="center"
					>
						<RCCGrid container item
							xs={12} 
							sm={6}
						>
							<RCCInput 
								name="indicator.iqc001Roteixodir"
								register={register}
								label={translate("Rótulo")}
								defaultValue={indicator.iqc001Roteixodir}
								error={errors?.indicator?.iqc001Roteixodir}
								maxLength={60}
							/>
						</RCCGrid>
						<RCCGrid container item 
							styleInline={FieldOnRight}
							wrap="nowrap"
							sm={6}
						>
							<RCCGrid container item >
								<InputNumber 
									name="indicator.iqc001Escalamindir"
									control={control}
									decimalScale={0}
									label={translate("Escala mín.")}
									defaultValue={indicator.iqc001Escalamindir}
									error={errors?.indicator?.iqc001Escalamindir}
									maxLength={10}
								/>
							</RCCGrid>
							<RCCGrid container item styleInline={FieldAlwaysOnRight}>
								<InputNumber 
									name="indicator.iqc001Escalamaxdir"
									control={control}
									decimalScale={0}
									label={translate("Escala max.")}
									defaultValue={indicator.iqc001Escalamaxdir}
									error={errors?.indicator?.iqc001Escalamaxdir}
									maxLength={10}
								/>
							</RCCGrid>
						</RCCGrid>
					</RCCGrid>
				</RCCGrid>
			</RCCGrid>
		</RCCGrid>
	);
};
export default TabGraphic;
